// Cálculos clínicos que el papel del Anexo 1 MSP deriva de otros campos y que antes se tecleaban a mano: IMC, total de la escala de
// Glasgow, sumas del balance hídrico, conteo de componentes sanguíneos y duración de una transfusión. Todo puro y sin React: los
// formularios (002, 003, 057, 113, 114, 115, 119, 122...) los muestran de solo lectura y el `Par[]` del PDF firmado imprime el valor
// calculado. Un dato ausente o ilegible nunca produce un resultado parcial inventado: devuelve `undefined`/`null`/`''`.
import { aNumero } from './signosVitalesToma';

// ---------- IMC ----------

/** Talla mínima (cm) con la que se calcula el IMC: por debajo es una talla a medio teclear ("1", "16" de "160") o un error. */
export const TALLA_MINIMA_IMC_CM = 30;
/** Talla máxima (cm) con la que se calcula el IMC (por encima es un error de digitación). */
export const TALLA_MAXIMA_IMC_CM = 280;

/**
 * IMC = kg / m² redondeado a 1 decimal (0,1) si hay peso y talla válidos (mismo criterio numérico que las `Observation` de peso y talla:
 * `aNumero`, lo que se ve es lo que se guarda). Se ignoran tallas a medio teclear o fuera de rango (`TALLA_MINIMA_IMC_CM`..`TALLA_MAXIMA_IMC_CM`)
 * y pesos no positivos.
 */
export function imcDe(peso: string | undefined, talla: string | undefined): number | undefined {
  const kg = aNumero(peso);
  const cm = aNumero(talla);
  if (kg === undefined || cm === undefined || kg <= 0 || cm < TALLA_MINIMA_IMC_CM || cm > TALLA_MAXIMA_IMC_CM) return undefined;
  return Math.round((kg / ((cm / 100) * (cm / 100))) * 10) / 10;
}

/** `imcDe` como texto para un campo de solo lectura (`''` mientras no haya peso y talla válidos). */
export function imcTexto(peso: string | undefined, talla: string | undefined): string {
  const imc = imcDe(peso, talla);
  return imc === undefined ? '' : String(imc);
}

// ---------- Glasgow ----------

export interface RangoEscala {
  readonly min: number;
  readonly max: number;
}

/** Rangos de cada componente de la escala de coma de Glasgow: ocular 1-4, verbal 1-5, motor 1-6 (total 3-15). */
export const RANGOS_GLASGOW = {
  ocular: { min: 1, max: 4 },
  verbal: { min: 1, max: 5 },
  motor: { min: 1, max: 6 },
} as const satisfies Record<string, RangoEscala>;
export type ComponenteGlasgow = keyof typeof RANGOS_GLASGOW;

/** `true` si el texto es un entero dentro del rango (los componentes de Glasgow no tienen decimales). */
export function enRangoEntero(valor: string | undefined, rango: RangoEscala): boolean {
  const n = aNumero(valor);
  return n !== undefined && Number.isInteger(n) && n >= rango.min && n <= rango.max;
}

/** `true` si el campo tiene algo escrito y NO es un valor válido del rango — para marcarlo en pantalla sin bloquear el guardado. */
export function fueraDeRango(valor: string | undefined, rango: RangoEscala): boolean {
  return (valor ?? '').trim() !== '' && !enRangoEntero(valor, rango);
}

/** Total de Glasgow (3-15) si los 3 componentes están cargados y dentro de su rango; `null` si falta alguno o alguno es inválido. */
export function totalGlasgow(ocular: string | undefined, verbal: string | undefined, motor: string | undefined): number | null {
  if (!enRangoEntero(ocular, RANGOS_GLASGOW.ocular) || !enRangoEntero(verbal, RANGOS_GLASGOW.verbal) || !enRangoEntero(motor, RANGOS_GLASGOW.motor)) return null;
  return Number(ocular!.trim().replace(',', '.')) + Number(verbal!.trim().replace(',', '.')) + Number(motor!.trim().replace(',', '.'));
}

// ---------- Balance hídrico (sumas de las filas horarias) ----------

/**
 * Redondea a 2 decimales (evita 0,1+0,2=0,30000000000000004) y lo pasa a texto sin ceros sobrantes, con coma decimal: es como la enfermera
 * teclea los volúmenes ("80,5") y como se lee en el balance impreso; `volumenDe`/`aNumero` la leen de vuelta.
 */
export function numeroATexto(n: number): string {
  return String(Math.round(n * 100) / 100).replace('.', ',');
}

/**
 * Número de una celda de volumen (mL): como `aNumero`, pero "1.000" / "2.500.000" se leen como miles (separador de miles a la española) y no
 * como 1 o 2,5: un volumen con exactamente 3 decimales no es realista y equivocarse sería un error de 1000 veces en el balance.
 */
function volumenDe(texto: string): number | undefined {
  const limpio = texto.trim();
  return /^\d{1,3}(\.\d{3})+$/.test(limpio) ? aNumero(limpio.replace(/\./g, '')) : aNumero(limpio);
}

/**
 * Suma las celdas numéricas de las filas indicadas (todas las horas). Las celdas vacías o no numéricas se ignoran; `undefined` si ninguna
 * celda de esas filas es numérica (para no imprimir un "0" inventado en un balance sin datos).
 */
export function sumaFilasHorarias(estado: Readonly<Record<string, Readonly<Record<string, string>>>>, claves: readonly string[]): number | undefined {
  let suma = 0;
  let hayDato = false;
  for (const clave of claves) {
    for (const valor of Object.values(estado[clave] ?? {})) {
      const n = volumenDe(valor);
      if (n === undefined) continue;
      suma += n;
      hayDato = true;
    }
  }
  return hayDato ? suma : undefined;
}

export interface ConfigBalanceHidrico<C extends string> {
  /** Cada total del papel → filas horarias que lo componen. */
  readonly totalesIngresos: Readonly<Partial<Record<C, readonly string[]>>>;
  readonly totalesEgresos: Readonly<Partial<Record<C, readonly string[]>>>;
  readonly sumaIngresos: C;
  readonly sumaEgresos: C;
  readonly balanceDiario: C;
}

/**
 * Valor que se ve e imprime de cada campo del balance hídrico: lo que la enfermera SOBRESCRIBIÓ a mano (`manual`, no vacío) o, si no,
 * el valor calculado. El cálculo va en cascada sobre los valores efectivos: total (suma de sus filas horarias) → suma de ingresos /
 * egresos (suma de sus totales) → balance diario (ingresos − egresos, solo si hay ambas sumas). Los campos que el papel no permite derivar
 * de una sola hoja (balance TOTAL/GLOBAL: acumulado de varios días) quedan como los escribió el usuario.
 * Un valor manual que no es número ("NA") se muestra tal cual y no entra a las sumas.
 */
export function balanceHidricoEfectivo<C extends string>(
  config: ConfigBalanceHidrico<C>,
  campos: readonly C[],
  manual: Readonly<Record<C, string>>,
  ingresos: Readonly<Record<string, Readonly<Record<string, string>>>>,
  egresos: Readonly<Record<string, Readonly<Record<string, string>>>>,
): Record<C, string> {
  const efectivo = Object.fromEntries(campos.map((c) => [c, (manual[c] ?? '').trim()])) as Record<C, string>;
  const completar = (clave: C, calculado: number | undefined): void => {
    if (efectivo[clave] === '' && calculado !== undefined) efectivo[clave] = numeroATexto(calculado);
  };
  const totales = (tabla: Readonly<Partial<Record<C, readonly string[]>>>, estado: Readonly<Record<string, Readonly<Record<string, string>>>>): C[] => {
    const claves = Object.keys(tabla) as C[];
    for (const clave of claves) completar(clave, sumaFilasHorarias(estado, tabla[clave] ?? []));
    return claves;
  };
  const numeroDe = (clave: C): number | undefined => volumenDe(efectivo[clave]);
  const sumar = (claves: readonly C[]): number | undefined => {
    const valores = claves.map(numeroDe).filter((n): n is number => n !== undefined);
    return valores.length === 0 ? undefined : valores.reduce((a, b) => a + b, 0);
  };
  const clavesIngresos = totales(config.totalesIngresos, ingresos);
  const clavesEgresos = totales(config.totalesEgresos, egresos);
  completar(config.sumaIngresos, sumar(clavesIngresos));
  completar(config.sumaEgresos, sumar(clavesEgresos));
  const sumaIng = numeroDe(config.sumaIngresos);
  const sumaEgr = numeroDe(config.sumaEgresos);
  completar(config.balanceDiario, sumaIng !== undefined && sumaEgr !== undefined ? sumaIng - sumaEgr : undefined);
  return efectivo;
}

// ---------- 122: componentes enviados y duración de la transfusión ----------

/** Cantidad de filas de la tabla con al menos una celda escrita (componentes sanguíneos enviados). */
export function contarFilasConDatos(estado: Readonly<Record<string, Readonly<Record<string, string>>>>, filas: readonly string[], columnas: readonly string[]): number {
  return filas.filter((f) => columnas.some((c) => (estado[f]?.[c] ?? '').trim() !== '')).length;
}

/** Minutos desde 00:00 de una hora "H:MM"/"HH:MM" (también "H.MM" o "HHhMM"); `undefined` si no es una hora válida. */
export function minutosDeHora(texto: string | undefined): number | undefined {
  const m = /^(\d{1,2})\s*[:.h]\s*(\d{2})$/i.exec((texto ?? '').trim());
  if (!m) return undefined;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 60 + min : undefined;
}

/**
 * Tiempo total entre hora de inicio y hora finalizada, como "1 h 30 min" o "45 min". Si la hora final es menor que la inicial se
 * entiende que pasó la medianoche. Horas iguales o ilegibles: `''` (no hay forma de saber si fueron 0 min o 24 h).
 */
export function duracionEntreHoras(inicio: string | undefined, fin: string | undefined): string {
  const a = minutosDeHora(inicio);
  const b = minutosDeHora(fin);
  if (a === undefined || b === undefined || a === b) return '';
  const minutos = b > a ? b - a : b + 24 * 60 - a;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return h === 0 ? `${m} min` : `${h} h ${String(m).padStart(2, '0')} min`;
}
